import { useEffect } from "react";
import { motion } from "framer-motion";
import { BUSINESS } from "@/data/site";
import { preselectService } from "@/lib/scroll";
import { useLang } from "@/i18n";
import Icon from "@/components/Icon";
import ProductArt from "@/components/art/ProductArt";
import { EASE } from "@/components/Reveal";

export default function ProductModal({ product, serviceName, onClose }) {
    const { t } = useLang();
    const tr = t.products.items[product.id];
    const d = t.products.details[product.id];

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        window.__lenis?.stop();
        return () => {
            window.removeEventListener("keydown", onKey);
            window.__lenis?.start();
        };
    }, [onClose]);

    return (
        <motion.div
            data-testid="product-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-8"
            role="dialog"
            aria-modal="true"
            aria-label={tr.name}
        >
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
            <motion.div
                initial={{ opacity: 0, y: 40, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 30, scale: 0.97 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-3xl border border-ink/10 bg-surface shadow-[0_40px_120px_rgba(0,0,0,0.6)]"
            >
                <button
                    data-testid="modal-close"
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full glass flex items-center justify-center text-ink hover:text-cyan2 transition-colors"
                >
                    <Icon name="x" size={18} />
                </button>

                <div
                    className="relative h-44 md:h-52 overflow-hidden rounded-t-3xl flex items-center justify-center"
                    style={{ background: `radial-gradient(circle at 50% 65%, ${product.accent}1f, transparent 70%)` }}
                >
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-64 h-full">
                            <ProductArt type={product.art} accent={product.accent} />
                        </div>
                    </div>
                    <img
                        src={`/images/products/${product.id}.png`}
                        alt={`${tr.name} — Farakka Broadband, Murshidabad`}
                        onError={(e) => e.currentTarget.remove()}
                        className="absolute inset-0 w-full h-full object-contain p-3"
                    />
                </div>

                <div className="p-6 md:p-8">
                    <span className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-ice/40">
                        {tr.category}
                    </span>
                    <h3 className="font-heading text-2xl md:text-3xl font-bold text-ink mt-2">
                        {tr.name}
                    </h3>
                    <p className="mt-2 text-sm md:text-base text-ice/60 leading-relaxed">
                        {tr.description}
                    </p>

                    {d.models && (
                        <div className="mt-6 space-y-3">
                            {d.models.map((m, i) => (
                                <div
                                    key={m.name}
                                    data-testid={`modal-model-${i}`}
                                    className="rounded-xl border border-ink/8 bg-elevated/70 p-4"
                                >
                                    <div className="flex flex-wrap items-baseline gap-x-3">
                                        <span className="font-heading font-semibold text-ink">{m.name}</span>
                                        {m.tag && <span className="text-xs text-solar">{m.tag}</span>}
                                    </div>
                                    {m.note && <p className="mt-1 text-sm text-ice/60">{m.note}</p>}
                                    {m.features && (
                                        <div className="mt-2.5 flex flex-wrap gap-1.5">
                                            {m.features.map((f) => (
                                                <span
                                                    key={f}
                                                    className="rounded-full border border-cyan2/20 bg-cyan2/5 px-2.5 py-1 text-[11px] text-ice/75"
                                                >
                                                    {f}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {d.groups && (
                        <div className="mt-6 space-y-5">
                            {d.groups.map((g, gi) => (
                                <div key={g.title} data-testid={`modal-group-${gi}`}>
                                    <p className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-cyan2/70">
                                        {g.title}
                                    </p>
                                    <div className="mt-2 space-y-2">
                                        {g.items.map((m) => (
                                            <div
                                                key={m.name}
                                                className="rounded-xl border border-ink/8 bg-elevated/70 px-4 py-3"
                                            >
                                                <span className="font-heading font-semibold text-ink text-sm">{m.name}</span>
                                                {m.note && (
                                                    <span className="block sm:inline sm:ml-2 text-xs text-ice/55">{m.note}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {d.points && (
                        <ul className="mt-6 space-y-2.5">
                            {d.points.map((pt) => (
                                <li key={pt} className="flex items-start gap-2.5 text-sm text-ice/75">
                                    <Icon name="check" size={16} className="text-cyan2 shrink-0 mt-0.5" />
                                    {pt}
                                </li>
                            ))}
                        </ul>
                    )}

                    {d.note && (
                        <p className="mt-5 text-xs md:text-sm text-ice/50 border-t border-ink/5 pt-4">
                            {d.note}
                        </p>
                    )}

                    {d.brochures && (
                        <div className="mt-4 space-y-2.5 border-t border-ink/10 pt-4">
                            {d.brochures.map((b, i) => (
                                <a
                                    key={b.file}
                                    data-testid={`modal-brochure-${i}`}
                                    href={b.file}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2.5 text-sm font-semibold text-electric hover:text-cyan2 transition-colors"
                                >
                                    <Icon name="download" size={15} className="shrink-0" />
                                    {b.label}
                                </a>
                            ))}
                        </div>
                    )}

                    {product.id === "smarten-inverter" && (
                        <a
                            data-testid="modal-catalogue-download"
                            href="/docs/Smarten-Product-Catalogue-2021-22.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-solar hover:text-ink transition-colors"
                        >
                            <Icon name="download" size={15} />
                            {t.products.downloadCatalogue}
                        </a>
                    )}

                    <div className="mt-7 flex flex-wrap gap-3">
                        <a
                            data-testid="modal-call"
                            href={BUSINESS.tel}
                            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-electric to-cyan2 px-6 py-3 font-bold text-ink text-sm hover:shadow-[0_0_35px_rgba(0,207,255,0.45)] hover:scale-[1.03] active:scale-95 transition-all duration-300"
                        >
                            <Icon name="phone" size={15} />
                            {t.products.modal.call} {BUSINESS.phoneDisplay}
                        </a>
                        <button
                            data-testid="modal-enquire"
                            onClick={() => {
                                onClose();
                                preselectService(serviceName);
                            }}
                            className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-6 py-3 font-semibold text-ink text-sm hover:border-cyan2 hover:text-cyan2 transition-colors duration-300"
                        >
                            {t.products.modal.enquire}
                            <Icon name="arrow" size={15} />
                        </button>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}
